import React, { useState, useEffect } from "react";
import { getSavedSearches, toggleSavedSearch, deleteSavedSearch } from "../../api";
import { BellRing, Search, Trash2, Mail, MapPin, BedDouble, PoundSterling, RefreshCw } from "lucide-react";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const money = (n) => (n ? `£${Number(n).toLocaleString()}` : "any");

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 bg-white border border-[#f0ece5] rounded-xl px-4 py-3" data-testid={`saved-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="w-9 h-9 rounded-lg bg-[#faf7f2] grid place-items-center text-[#b8935f]"><Icon size={18} /></div>
    <div>
      <div className="text-[20px] font-semibold text-[#2b2b2b] leading-none">{value}</div>
      <div className="text-[12px] text-[#8a8a8a] mt-1">{label}</div>
    </div>
  </div>
);

const AdminSavedSearches = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, totalMatchesFired: 0, onMarketListings: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getSavedSearches();
      setItems(d.items || []);
      setStats(d.stats || {});
    } catch (e) {
      toast({ title: "Couldn't load saved searches", variant: "destructive" });
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onToggle = async (s) => {
    const next = !s.active;
    setItems((xs) => xs.map((x) => (x.id === s.id ? { ...x, active: next } : x)));
    try {
      await toggleSavedSearch(s.id, next);
      toast({ title: next ? "Alert enabled" : "Alert paused" });
    } catch {
      setItems((xs) => xs.map((x) => (x.id === s.id ? { ...x, active: !next } : x)));
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete the saved search for ${s.name}? This can't be undone.`)) return;
    try {
      await deleteSavedSearch(s.id);
      setItems((xs) => xs.filter((x) => x.id !== s.id));
      toast({ title: "Saved search deleted" });
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-saved-searches">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-[22px] font-semibold text-[#2b2b2b] flex items-center gap-2"><BellRing size={20} className="text-[#b8935f]" /> Buyer Alerts</h2>
          <p className="text-[13px] text-[#8a8a8a] mt-1">Every saved search buyers registered, with live match counts and how many alert emails each has fired.</p>
        </div>
        <button onClick={load} data-testid="saved-refresh-btn" className="flex items-center gap-2 text-[13px] px-3 py-2 rounded-lg border border-[#f0ece5] text-[#6a6a6a] hover:bg-[#faf7f2]">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Search} label="Total alerts" value={stats.total || 0} />
        <StatCard icon={BellRing} label="Active" value={stats.active || 0} />
        <StatCard icon={Mail} label="Emails fired" value={stats.totalMatchesFired || 0} />
        <StatCard icon={PoundSterling} label="On-market listings" value={stats.onMarketListings || 0} />
      </div>

      {loading ? (
        <div className="py-12 text-center text-[#8a8a8a] text-[14px]">Loading buyer alerts…</div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-[#8a8a8a] text-[14px] bg-white border border-[#f0ece5] rounded-xl">No saved searches yet. Buyers register alerts from the Find a Property page.</div>
      ) : (
        <div className="bg-white border border-[#f0ece5] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-[#faf7f2] text-[#8a8a8a] text-left text-[12px] uppercase tracking-wide">
                  <th className="px-4 py-3 font-medium">Buyer</th>
                  <th className="px-4 py-3 font-medium">Criteria</th>
                  <th className="px-4 py-3 font-medium text-center">Matches now</th>
                  <th className="px-4 py-3 font-medium text-center">Emails fired</th>
                  <th className="px-4 py-3 font-medium">Registered</th>
                  <th className="px-4 py-3 font-medium text-center">Active</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-t border-[#f5f1ea] hover:bg-[#fdfbf8]" data-testid={`saved-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#2b2b2b]">{s.name || "Unknown buyer"}</div>
                      <div className="text-[12px] text-[#8a8a8a]">{s.email || "—"}</div>
                      {s.phone ? <div className="text-[12px] text-[#a8a8a8]">{s.phone}</div> : null}
                    </td>
                    <td className="px-4 py-3 text-[#4a4a4a]">
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] ${s.lookingFor === "rent" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{s.lookingFor === "rent" ? "To rent" : "To buy"}</span>
                        <span className="inline-flex items-center gap-1"><MapPin size={13} /> {s.area || "any area"}</span>
                        <span className="inline-flex items-center gap-1"><BedDouble size={13} /> {s.minBeds ? `${s.minBeds}+` : "any"}</span>
                        <span className="inline-flex items-center gap-1"><PoundSterling size={13} /> {money(s.maxPrice)}</span>
                      </div>
                      {s.notes ? <div className="text-[12px] text-[#a8a8a8] mt-1 italic">“{s.notes}”</div> : null}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block min-w-[28px] px-2 py-1 rounded-lg font-semibold ${s.currentMatches > 0 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{s.currentMatches}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-[#4a4a4a]">{s.matchesNotified}</td>
                    <td className="px-4 py-3 text-[#8a8a8a]">{fmtDate(s.createdAt)}</td>
                    <td className="px-4 py-3 text-center">
                      <Switch checked={s.active} onCheckedChange={() => onToggle(s)} data-testid={`saved-toggle-${s.id}`} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => onDelete(s)} data-testid={`saved-delete-${s.id}`} className="text-[#c0392b] hover:bg-red-50 p-2 rounded-lg" title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSavedSearches;
